/**
 * The calls of the search family under `/api/v1/crm` (spec-63 E2): register, search, views and
 * pins, refs, bulk actions and export. Contacts and organisations share one shape and differ in
 * the collection path only.
 */
import { crmDownload, crmRequest } from '../http'
import { normalizeFilter } from './codec'
import { isUuid } from './validate'
import type {
  BulkResult,
  CrmObject,
  ExportBody,
  QueryBody,
  Refs,
  Register,
  SearchBody,
  SearchPage,
  ViewDef,
  ViewInput,
  ViewPatch,
  ViewsResponse,
} from './types'

const PATHS: Record<CrmObject, string> = { contacten: '/contacts', organisaties: '/organisations', afspraken: '/agreements', lijsten: '/lists' }
/** The most ids of one kind that `GET /refs` takes. */
const MAX_REFS = 100

const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

/** The latest imports as filter choices ("<bestand> · <datum>"; the option source `import_batches`). */
export function listImportOptions(): Promise<{ id: string; name: string }[]> {
  return crmRequest('/imports/options')
}

export function getRegister(object: CrmObject): Promise<Register> {
  return crmRequest(`/properties?object=${object}`)
}

export function searchRecords<T>(object: CrmObject, body: SearchBody): Promise<SearchPage<T>> {
  return crmRequest(`${PATHS[object]}/search`, post(body))
}

/** How many records match: a search for the total only. */
export async function countRecords(object: CrmObject, query: QueryBody): Promise<number> {
  const page = await searchRecords<unknown>(object, { query, page_size: 0 })
  return page.total
}

// The server writes `"value": null` on a condition that takes none; the app has no value there.
const withCleanQuery = (view: ViewDef): ViewDef => ({ ...view, query: normalizeFilter(view.query) })

export async function listViews(object: CrmObject): Promise<ViewsResponse> {
  const data = await crmRequest<ViewsResponse>(`/views?object=${object}`)
  return { ...data, system: data.system.map(withCleanQuery), views: data.views.map(withCleanQuery) }
}

export async function createView(input: ViewInput): Promise<ViewDef> {
  return withCleanQuery(await crmRequest<ViewDef>('/views', post(input)))
}

export async function updateView(id: string, patch: ViewPatch): Promise<ViewDef> {
  return withCleanQuery(await crmRequest<ViewDef>(`/views/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }))
}

export function deleteView(id: string): Promise<void> {
  return crmRequest(`/views/${id}`, { method: 'DELETE' })
}

export async function restoreView(id: string): Promise<ViewDef> {
  return withCleanQuery(await crmRequest<ViewDef>(`/views/${id}/restore`, { method: 'POST' }))
}

/** Replaces the pinned views of an object, in order. */
export function setPins(object: CrmObject, viewIds: string[]): Promise<{ pins: string[] }> {
  return crmRequest(`/views/pins?object=${object}`, { method: 'PUT', body: JSON.stringify({ view_ids: viewIds }) })
}

/**
 * The names behind organisation and person ids, for filter chips; ids that no longer exist are
 * simply not in the answer. An id that is no uuid is left out: the server would answer 422 for
 * the whole call and the names of the good ids would be lost with it. No call without ids. The
 * server takes 100 of a kind per call, so a longer list goes over several.
 */
export async function getRefs({
  organisationIds,
  personIds,
}: {
  organisationIds: readonly string[]
  personIds: readonly string[]
}): Promise<Refs> {
  const organisations = [...new Set(organisationIds.filter(isUuid))]
  const persons = [...new Set(personIds.filter(isUuid))]
  const refs: Refs = { organisations: [], persons: [] }
  const calls = Math.ceil(Math.max(organisations.length, persons.length) / MAX_REFS)
  const answers = await Promise.all(
    Array.from({ length: calls }, (_, call) => {
      const query = new URLSearchParams()
      const range = { start: call * MAX_REFS, end: (call + 1) * MAX_REFS }
      for (const id of organisations.slice(range.start, range.end)) query.append('organisation_ids', id)
      for (const id of persons.slice(range.start, range.end)) query.append('person_ids', id)
      return crmRequest<Refs>(`/refs?${query.toString()}`)
    }),
  )
  for (const answer of answers) {
    refs.organisations.push(...answer.organisations)
    refs.persons.push(...answer.persons)
  }
  return refs
}

/** One bulk action (`labels`, `status`, `delete`, …) on the selection in `body`. */
export function runBulk(object: CrmObject, action: string, body: Record<string, unknown>): Promise<BulkResult> {
  return crmRequest(`${PATHS[object]}/bulk/${action}`, post(body))
}

/** The xlsx export of a query, a selection and the visible columns, saved as `filename`. */
export function exportRecords(object: CrmObject, body: ExportBody, filename: string): Promise<void> {
  return crmDownload(`${PATHS[object]}/export`, body, filename)
}
